import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Copy } from "lucide-react";
import { cn } from "../../lib/cn";
import { BLOCK_SURFACE_FRAME, BLOCK_SURFACE_INSET } from "./blockSurface";

/**
 * CodeBlock - syntax-highlighted code with a copy button. Renderer for the
 * K2 `code` block (`docs/contracts/block-registry.md`): the platform's chat
 * `BlockRenderer` passes a `code` block's `props` straight through, so the
 * prop names match the block payload exactly.
 *
 * Block props (`{ language, source }`):
 *   - `language` - the language label (e.g. `"python"`, `"tsx"`). Drives the
 *     Prism grammar and the header label. Falls back to `"text"` (plain)
 *     when empty.
 *   - `source` - the code text. Required. Empty / whitespace-only renders a
 *     muted empty state rather than an empty highlighter shell.
 *
 * Client-only props (never part of the streamed block): `className`,
 * `filename`, `showLineNumbers`, `loading`, `error`, `copyable`.
 *
 * Uses `react-syntax-highlighter` (already shipped + externalized by the
 * build) with the Prism `oneDark` theme - the same stack the `Markdown`
 * primitive's fenced-code renderer uses, so chat code reads consistently
 * across the markdown body and standalone code blocks. The copy button is
 * feature-checked (`navigator.clipboard`), runs only in `onClick` (never at
 * render), and silently no-ops when unavailable - SSR-safe.
 *
 * Chrome is token-driven (`bg-card`, `border-border`, `text-muted-foreground`,
 * …); the highlighted body uses the Prism `oneDark` palette by design (a code
 * surface is intentionally its own dark canvas, matching `Markdown`).
 */

const codeBlockVariants = cva(
  cn(
    // Frame only — the header bar + the code canvas pad themselves so each
    // region's inset matches the surface radius and the rounded corners clip
    // cleanly over the first / last code line.
    "group/code relative my-0 overflow-hidden not-prose",
    BLOCK_SURFACE_FRAME,
  ),
);

export interface CodeBlockProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Language label + Prism grammar. Empty falls back to `"text"`. */
  language?: string;
  /** The code text. Required. Empty / whitespace renders an empty state. */
  source: string;
  /** Optional filename shown in the header in place of the language label. */
  filename?: string;
  /** Show a gutter of line numbers. Default `false`. */
  showLineNumbers?: boolean;
  /** Render a loading skeleton instead of content. */
  loading?: boolean;
  /** Render an error message instead of content. Pass `true` or a string. */
  error?: boolean | string;
  /** Show the copy button. Default `true`. */
  copyable?: boolean;
}

export const CodeBlock = React.forwardRef<HTMLDivElement, CodeBlockProps>(function CodeBlock(
  {
    className,
    language,
    source,
    filename,
    showLineNumbers = false,
    loading = false,
    error = false,
    copyable = true,
    ...rest
  },
  ref,
) {
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = React.useCallback(() => {
    // Feature-check - older Safari (pre-13.1) lacks `navigator.clipboard`.
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(source).catch(() => {
      // Permission denied / focus loss - silent no-op so the thread never
      // crashes; the user can still hand-select the code.
    });
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  }, [source]);

  if (loading) {
    return (
      <div
        ref={ref}
        className={cn(codeBlockVariants(), className)}
        aria-busy="true"
        data-slot="code-block"
        {...rest}
      >
        <div
          className={cn(
            "flex items-center justify-between border-b border-border/70 bg-card py-2.5",
            BLOCK_SURFACE_INSET,
          )}
        >
          <div className="h-3 w-16 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        </div>
        <div className={cn("space-y-2 bg-card py-4", BLOCK_SURFACE_INSET)}>
          <div className="h-3 w-[70%] animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-3 w-[85%] animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-3 w-[55%] animate-pulse rounded bg-muted motion-reduce:animate-none" />
        </div>
      </div>
    );
  }

  if (error) {
    const message = typeof error === "string" ? error : "Couldn't load this code block.";
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          codeBlockVariants(),
          "border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive",
          className,
        )}
        data-slot="code-block"
        {...rest}
      >
        {message}
      </div>
    );
  }

  const isEmpty = !source || /^\s*$/.test(source);
  const label = filename ?? (language && language.trim() ? language : undefined);

  return (
    <div
      ref={ref}
      className={cn(codeBlockVariants(), className)}
      data-slot="code-block"
      {...rest}
    >
      <div
        className={cn(
          "flex items-center justify-between border-b border-border/70 bg-card py-2.5 text-xs text-muted-foreground",
          BLOCK_SURFACE_INSET,
        )}
      >
        <span className="truncate font-mono">{label ?? "code"}</span>
        {copyable && !isEmpty ? (
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? "Copied to clipboard" : "Copy code"}
            className={cn(
              "flex items-center gap-1 rounded text-muted-foreground transition-colors hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {copied ? (
              <>
                <Check className="size-3" aria-hidden />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="size-3" aria-hidden />
                <span>Copy</span>
              </>
            )}
          </button>
        ) : null}
      </div>

      {isEmpty ? (
        <div className={cn("bg-card py-8 text-center text-sm text-muted-foreground", BLOCK_SURFACE_INSET)}>
          No code to show.
        </div>
      ) : (
        <SyntaxHighlighter
          style={oneDark}
          language={language && language.trim() ? language : "text"}
          PreTag="div"
          showLineNumbers={showLineNumbers}
          customStyle={{
            margin: 0,
            // The frame's `overflow-hidden` clips the rounded corners, so the
            // canvas fills flush (radius 0) and the first / last code line can
            // never be clipped by a corner of its own. Generous, even padding
            // (>= the surface radius) keeps code off the rounded edge.
            borderRadius: 0,
            padding: "1.25rem",
            fontSize: "0.85rem",
          }}
        >
          {source}
        </SyntaxHighlighter>
      )}
    </div>
  );
});

CodeBlock.displayName = "CodeBlock";

export { codeBlockVariants };
export type CodeBlockVariantProps = VariantProps<typeof codeBlockVariants>;
